import React, { useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * MultiSelectDropdown - reusable checkbox-list dropdown for selecting multiple values.
 * Used by Credentials Management (project filter) and Credential Details (Feature 027 -
 * editing a credential's project/brand list).
 */
const MultiSelectDropdown = ({ options = [], selectedValues = [], onChange, placeholder = "Select options" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectedOptions = options.filter(opt => selectedValues.includes(opt.value));
  // Feature 027 - with many selections (e.g. a credential with 20+ products), listing every
  // label wrapped the closed button across several lines with no visible chevron, looking
  // broken rather than like a dropdown. Show a compact summary instead once there are more
  // than a couple of selections.
  const MAX_LABELS_SHOWN = 2;
  let summaryText = placeholder;
  if (selectedOptions.length > 0) {
    summaryText = selectedOptions.length <= MAX_LABELS_SHOWN
      ? selectedOptions.map(opt => opt.label).join(', ')
      : `${selectedOptions.length} selected: ${selectedOptions.slice(0, MAX_LABELS_SHOWN).map(opt => opt.label).join(', ')}...`;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded p-2 text-sm text-left bg-white flex items-center justify-between gap-2 hover:bg-gray-50"
      >
        <span className={`truncate min-w-0 flex-1 ${selectedOptions.length > 0 ? 'text-gray-900' : 'text-gray-500'}`} title={selectedOptions.length > 0 ? selectedOptions.map(opt => opt.label).join(', ') : undefined}>
          {summaryText}
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50">
          <div className="max-h-64 overflow-y-auto p-2">
            {options.map(option => (
              <label key={option.value} className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleToggleOption(option.value)}
                  className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
          <div className="border-t border-gray-200 p-2 flex gap-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-3 rounded text-sm"
            >
              OK
            </button>
            <button
              type="button"
              onClick={() => {
                onChange([]);
                setIsOpen(false);
              }}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-900 font-medium py-2 px-3 rounded text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
